/* ---------------------------------------------------------------------------
   A model's components, and where each one is on the bridge.

   A record names the part it is about by the model's component id (LC-14).
   For that id to place the record — span 2, girder 3 of 5; bent 1, pile 4 —
   the export has to say where each component is, and that is the
   `Components` block a CSiBridge cloud model carries:

     "Components": [
       { "Guid": "…", "Parent": "…", "Kind": "Girder", "Name": "G-3",
         "Span": 2, "Support": null, "Index": 3, "Count": 5 }, …
     ]

   `Guid` is the component's own identity, the same in every export of the
   model and across a regeneration in CSiBridge; every `Mesh` part names the
   component it draws by it (`guid`). `Span` and `Support` are 1-based, a
   support counted along the bridge with the abutments; `Index` and `Count`
   are a component's place among its siblings of one kind — girder 3 of 5.

   AN EXPORT FROM BEFORE THE BLOCK has none, and its mesh parts are named by
   path ids ("Span-2/Girder-3", "Bent-1/Foundation-1/Pile-4"). The directory is
   then read out of those paths, so the records made on such a model still
   place. Nothing here guesses at a GUID: a part with neither a block entry nor
   a path places nowhere, and its records read by their label, as before.
   --------------------------------------------------------------------------- */

export interface ModelComponent {
  /** The component id records store: the export's GUID, or a path on an older export. */
  id: string;
  parent: string | null;
  /** What it is, in the export's words: Girder, DeckSlab, Column, Pile, Bearing … */
  kind: string;
  name: string;
  /** The span it is on, 1-based, or null. */
  span: number | null;
  /** The support it is on, 1-based along the bridge with the abutments, or null. */
  support: number | null;
  /** Its place among siblings of its kind, 1-based: girder 3. */
  index: number | null;
  /** How many siblings of its kind there are: of 5 girders. */
  count: number | null;
}

/** Where a record's component is: what a defect read carries as `place`. */
export type ComponentPlace = Pick<ModelComponent, 'kind' | 'name' | 'span' | 'support' | 'index' | 'count'>;

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const int = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);

/** Whether a kind is the member a superstructure rating rates. */
export function isGirderKind(kind: string): boolean {
  /* The engine's own class names: a PSCGirder is one, a PSCGirderSlabSection is
     the deck, a UniformDepthBentCapBeam and an AbutmentBearingBeam are not. */
  return /girder|beam|stringer/i.test(kind) && !/cap|bearing|slab|section|diaphragm/i.test(kind);
}

/** Whether a kind is a deck slab. */
export function isSlabKind(kind: string): boolean {
  return /slab|deck/i.test(kind) && !/approach|joint|surfac|walk/i.test(kind);
}

/** The cloud model among an import's texts: the document that has a mesh or a directory. */
export function cloudDocument(texts: readonly string[]): Rec | null {
  for (const t of texts) {
    try {
      const doc = rec(JSON.parse(t));
      if (doc['Components'] !== undefined || doc['Mesh'] !== undefined) return doc;
    } catch {
      /* not JSON — an alignment */
    }
  }
  return null;
}

/** The components an export states, or derives from its path ids where it states none. */
export function componentsOf(doc: Rec): ModelComponent[] {
  const block = arr(doc['Components']).map(rec);
  if (block.length > 0) {
    const out: ModelComponent[] = [];
    for (const c of block) {
      const id = str(c['Guid']);
      if (id === null) continue;
      out.push({
        id,
        parent: str(c['Parent']),
        kind: str(c['Kind']) ?? 'Part',
        name: str(c['Name']) ?? id,
        span: int(c['Span']),
        support: int(c['Support']),
        index: int(c['Index']),
        count: int(c['Count']),
      });
    }
    return out;
  }
  return fromPaths(doc);
}

/** The supports of an export in order along the bridge, by name: Abutment-1, Bent-1 … */
function supportOrder(doc: Rec): Map<string, number> {
  const bents = arr(doc['Bents'])
    .map(rec)
    .map((b) => ({ name: str(b['Id']) ?? str(b['Name']), station: typeof b['Station'] === 'number' ? (b['Station'] as number) : null }))
    .filter((b): b is { name: string; station: number | null } => b.name !== null);
  const sorted = bents.every((b) => b.station !== null) ? [...bents].sort((a, b) => a.station! - b.station!) : bents;
  return new Map(sorted.map((b, i) => [b.name, i + 1]));
}

/** An older export's directory, out of its mesh parts' path ids. */
function fromPaths(doc: Rec): ModelComponent[] {
  const parts = arr(rec(doc['Mesh'])['Parts']).map(rec);
  const supports = supportOrder(doc);
  const byId = new Map<string, ModelComponent>();
  for (const p of parts) {
    const id = str(p['id']);
    if (id === null || !id.includes('/') || byId.has(id)) continue;
    const steps = id.split('/');
    const head = steps[0]!;
    const last = steps[steps.length - 1]!;
    const span = /^Span-(\d+)$/.exec(head);
    const type = str(p['type']) ?? 'Part';
    byId.set(id, {
      id,
      parent: steps.length > 1 ? steps.slice(0, -1).join('/') : null,
      kind: /girder/i.test(last) ? 'Girder' : type,
      name: str(p['name']) ?? last,
      span: span ? Number(span[1]) : null,
      support: span ? null : (supports.get(head) ?? null),
      index: /-(\d+)$/.test(last) ? Number(/-(\d+)$/.exec(last)![1]) : null,
      count: null,
    });
  }
  /* Count siblings of one kind under one parent: girder 3 OF 5. */
  const groups = new Map<string, ModelComponent[]>();
  for (const c of byId.values()) {
    const key = `${c.parent ?? ''}|${c.kind}|${c.span ?? ''}`;
    groups.set(key, [...(groups.get(key) ?? []), c]);
  }
  for (const group of groups.values()) {
    const n = group.filter((c) => c.index !== null).length;
    for (const c of group) if (c.index !== null) c.count = n;
  }
  return [...byId.values()];
}

/**
 * The components a rating of one span and girder position rates: that span's
 * girders, the outer two for an exterior rating and the rest for an interior
 * one; its slab for a flat-slab family. Empty where the directory cannot say.
 */
export function ratedComponents(
  components: readonly ModelComponent[],
  family: string,
  spanIndex: number | null,
  position: string | null,
): ModelComponent[] {
  const onSpan = components.filter((c) => spanIndex === null || c.span === spanIndex);
  if (family === 'RCFS' || family === 'RCDECK') return onSpan.filter((c) => isSlabKind(c.kind));
  return onSpan.filter((c) => {
    if (!isGirderKind(c.kind)) return false;
    if (position === null || c.index === null || c.count === null) return true;
    const outer = c.index === 1 || c.index === c.count;
    return position === 'exterior' ? outer : !outer;
  });
}
